import { useLayoutEffect, useState } from "react";
import type { PositionAnchor } from "./anchor.js";

export type PositionAlign = "start" | "center" | "end";
export type PositionSide = "top" | "bottom" | "left" | "right";

export interface UsePositionOptions {
  open: boolean;
  /** The anchor: usually the trigger, or any other element, or a virtual
   *  rect (see `./anchor.ts`). Only its rect is read. */
  triggerRef: React.RefObject<PositionAnchor | null>;
  contentRef: React.RefObject<HTMLElement | null>;
  /** Preferred side of the trigger (default "bottom"). top/bottom flip
   *  vertically when clipped; left/right flip horizontally. */
  side?: PositionSide;
  /** Cross-axis alignment against the trigger (default "end"); applies to
   *  top/bottom sides. left/right always center vertically. */
  align?: PositionAlign;
  /** Gap between trigger and content in px (default 8). */
  sideOffset?: number;
  /** Min-width the content to the trigger width (Select-style listboxes). */
  matchWidth?: boolean;
  /** Viewport edge margin in px (default 8). */
  margin?: number;
  /** Cap the content height (px). When set, placement uses the CLAMPED height
   *  and the returned `maxHeight` enforces it — so the consumer must NOT also
   *  apply a CSS max-height (a double-cap places the box for the full
   *  scrollHeight while it renders shorter, detaching it from the trigger). */
  maxHeight?: number;
  /** Re-measure each animation frame while open. Use only for anchors moving
   *  through CSS transforms; scroll and resize are already tracked normally. */
  followAnchor?: boolean;
}

export interface PositionResult {
  top: number;
  left: number;
  maxHeight?: number | undefined;
  width?: number | undefined;
}

/**
 * Fixed-position placement against a trigger (strategy §14.3, extracted from
 * Popover — the best of the three implementations that existed). Prefers
 * below; flips above when below clips and above has more room; constrains
 * maxHeight when neither fits; clamps horizontally to the viewport.
 * Recomputes on resize and any scroll (capture). Returns null until measured
 * — render content hidden until then.
 */
export function usePosition({
  open,
  triggerRef,
  contentRef,
  side = "bottom",
  align = "end",
  sideOffset = 8,
  matchWidth = false,
  margin = 8,
  maxHeight: userMaxHeight,
  followAnchor = false,
}: UsePositionOptions): PositionResult | null {
  const [pos, setPos] = useState<PositionResult | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: clearing the resolved position when the overlay closes. This is the shared positioning engine (Popover/Select/Combobox/DatePicker/Tooltip); restructuring it for one warning risks every consumer.
      setPos(null);
      return;
    }
    const commit = (next: PositionResult) => {
      setPos((current) =>
        current?.top === next.top &&
        current.left === next.left &&
        current.maxHeight === next.maxHeight &&
        current.width === next.width
          ? current
          : next,
      );
    };
    const compute = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const r = trigger.getBoundingClientRect();
      const contentEl = contentRef.current;
      const cw = matchWidth ? r.width : (contentEl?.offsetWidth ?? 200);
      // scrollHeight so an applied maxHeight (outer div scrolls) doesn't
      // shrink the measurement next pass and oscillate measure↔clamp.
      const chFull = contentEl?.scrollHeight ?? 0;
      // A caller-supplied cap is the height the content will actually render at
      // — place against THAT, not the full scrollHeight, or the box detaches
      // from the trigger by (scrollHeight − cap).
      const ch =
        userMaxHeight != null ? Math.min(chFull, userMaxHeight) : chFull;
      const capsHeight = userMaxHeight != null && chFull > userMaxHeight;

      /* Horizontal sides: flip left↔right when clipped; center vertically. */
      if (side === "left" || side === "right") {
        let top = r.top + r.height / 2 - ch / 2;
        const maxTop = window.innerHeight - ch - margin;
        if (top > maxTop) top = maxTop;
        if (top < margin) top = margin;
        const spaceLeft = r.left - sideOffset - margin;
        const spaceRight = window.innerWidth - r.right - sideOffset - margin;
        const fitsLeft = cw <= spaceLeft;
        const fitsRight = cw <= spaceRight;
        const placeLeft =
          side === "left"
            ? fitsLeft || spaceLeft >= spaceRight
            : !(fitsRight || spaceRight >= spaceLeft);
        const rawLeft = placeLeft
          ? r.left - sideOffset - cw
          : r.right + sideOffset;
        const maxLeft = Math.max(margin, window.innerWidth - cw - margin);
        const left = Math.max(margin, Math.min(rawLeft, maxLeft));
        commit({
          top,
          left,
          maxHeight: capsHeight ? userMaxHeight : undefined,
          width: matchWidth ? r.width : undefined,
        });
        return;
      }

      let left: number;
      if (align === "end") left = r.right - cw;
      else if (align === "start") left = r.left;
      else left = r.left + r.width / 2 - cw / 2;
      const maxLeft = window.innerWidth - cw - margin;
      if (left > maxLeft) left = maxLeft;
      if (left < margin) left = margin;

      const spaceBelow = window.innerHeight - r.bottom - sideOffset - margin;
      const spaceAbove = r.top - sideOffset - margin;
      let top: number;
      let maxHeight: number | undefined;
      const fitsBelow = ch <= spaceBelow;
      const fitsAbove = ch <= spaceAbove;
      const placeBelow =
        side === "top"
          ? !(fitsAbove || (!fitsBelow && spaceAbove >= spaceBelow))
          : fitsBelow || (!fitsAbove && spaceBelow >= spaceAbove);
      if (placeBelow) {
        top = r.bottom + sideOffset;
        if (!fitsBelow) maxHeight = Math.max(0, spaceBelow);
        else if (capsHeight) maxHeight = userMaxHeight;
      } else {
        const cap = fitsAbove ? ch : Math.max(0, spaceAbove);
        top = r.top - sideOffset - cap;
        if (!fitsAbove) maxHeight = cap;
        else if (capsHeight) maxHeight = userMaxHeight;
      }
      commit({ top, left, maxHeight, width: matchWidth ? r.width : undefined });
    };
    compute();
    // A matched dropdown follows a resizable rail and async result heights,
    // not just browser-window resize events.
    const observer =
      matchWidth && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(compute)
        : null;
    if (triggerRef.current instanceof Element)
      observer?.observe(triggerRef.current);
    if (contentRef.current) observer?.observe(contentRef.current);
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    let animationFrame = 0;
    const follow = () => {
      compute();
      animationFrame = window.requestAnimationFrame(follow);
    };
    if (followAnchor) animationFrame = window.requestAnimationFrame(follow);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
      observer?.disconnect();
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
    };
  }, [
    open,
    side,
    align,
    sideOffset,
    matchWidth,
    margin,
    userMaxHeight,
    triggerRef,
    contentRef,
    followAnchor,
  ]);

  return pos;
}
